import React from 'react';
import PropTypes from 'prop-types';
import { gql } from '@common/hooks';
import {
  Button,
  ButtonGroup,
  Currency,
  TimeWithTimeZone,
  Icon,
  Gutter,
  Text,
  Flex,
  Link,
} from '@common/components';
import { ExtendOffer } from 'works/legacy/components/extension-offer/extend-offer';
import { OfferPreviewCard } from '@applicant-flow/components';
import { OrganizationOnly } from 'works/legacy/components/auth-guard/organization-only';
import { AssignmentExtensionSummary } from 'works/legacy/components/assignment-extension-summary';
import { HistoryInfoFragment } from '@common/graphql';
import {
  AssignmentCancelReason,
  ExtensionOfferActionRetract,
} from '@working-experience/components';
import { buildDialogUrl } from '@common/services';
import { EXTENSION_OFFER_FRAGMENT } from '@working-experience/graphql';
import { STAFFING_REQUEST_APPLICATION_INFO_FRAGMENT } from '@applicant-flow/graphql';

export function AssignmentPane({ assignment }) {
  const EXTENSION_OFFER_END_STATES = {
    REJECTED: 'rejected',
    RETRACTED: 'retracted',
    CANCELLED: 'cancelled',
  };
  const EXTENSION_OFFER_ACTIONABLE_STATES = ['active', 'change_requested'];

  const offerPreviewDrawerUrl = buildDialogUrl('offer-preview-drawer', {
    props: { offerId: assignment?.offer?.id || '' },
  });

  return (
    <div className="assignment-pane">
      <div className="assignment-pane__header">
        <Gutter asPadding size="md">
          <Flex justify="end" align="center" fullWidth>
            <Text>
              Total Estimated Budget:{' '}
              <Text bold>
                <Currency value={assignment.estimatedBudget} />
              </Text>
            </Text>
            {assignment.canExtend.value && !assignment.activeExtensionOffer && (
              <Gutter direction="left" size="md">
                <ExtendOffer
                  assignment={assignment}
                  buttonType="primary"
                  buttonText="Extend Assignment"
                />
              </Gutter>
            )}
          </Flex>
        </Gutter>
      </div>

      {assignment.state === 'cancelled' && (
        <Gutter direction="bottom" size="md">
          <OfferPreviewCard
            key={`OfferPreviewCard-${assignment.id}-cancelled`}
            headerText={
              <>
                <span>
                  Assignment cancelled
                  {assignment.cancelledByUserName && (
                    <> by {assignment.cancelledByUserName}</>
                  )}
                  .{' '}
                  {!assignment.neverStarted && (
                    <>
                      <span>End date updated to</span>{' '}
                      <TimeWithTimeZone
                        date={assignment.finalEndDate}
                        variant="short_format"
                      />
                    </>
                  )}
                </span>

                <div className="text-light">
                  <AssignmentCancelReason
                    reasonName={assignment.cancelledReason}
                    details={assignment.cancelledDetail}
                  />
                </div>
              </>
            }
            action="cancelled"
            actionedAt={
              <TimeWithTimeZone
                date={assignment.stateChangedAt}
                variant="long_format"
              />
            }
            candidateName={assignment.worksProfile.fullName}
            actionerName={assignment.organization.name}
          />
        </Gutter>
      )}

      {[...assignment.extensionOffers]
        .sort((aa, bb) => new Date(bb.createdAt) - new Date(aa.createdAt))
        .map((extensionOffer) => {
          if (extensionOffer.state === 'accepted') {
            return (
              <Gutter
                key={`Gutter-${extensionOffer.id}`}
                direction="bottom"
                size="md"
              >
                <AssignmentExtensionSummary
                  staffingRequestAssignmentId={assignment.id}
                  assignmentState={assignment.state}
                  sourceHistory={extensionOffer.sourceHistory}
                  isExtension={true}
                  extensionReason={extensionOffer.extensionReason}
                  estimatedBudget={extensionOffer.sourceEstimatedBudget}
                  terms={extensionOffer.notVoidedSourceAssignmentTerms}
                  percentageComplete={extensionOffer.sourcePercentageComplete}
                  startDate={extensionOffer.sourceStartDate}
                  endDate={extensionOffer.sourceEndDate}
                  lengthInWeeks={extensionOffer.lengthInWeeks}
                  assignment={assignment}
                >
                  <OfferPreviewCard
                    key={`OfferPreviewCard-${extensionOffer.id}-extension`}
                    action="accepted"
                    isExtension={true}
                    actionedAt={
                      <TimeWithTimeZone
                        date={extensionOffer.stateChangedAt}
                        variant="long_format"
                      />
                    }
                    candidateName={assignment.worksProfile.fullName}
                    actionerName={assignment.supplier.name}
                  >
                    <Link
                      to={buildDialogUrl('extension-offer-preview-drawer', {
                        props: { extensionOfferId: extensionOffer.id },
                      })}
                    >
                      <Button size="md">View Offer</Button>
                    </Link>
                  </OfferPreviewCard>
                </AssignmentExtensionSummary>
              </Gutter>
            );
          }

          if (
            EXTENSION_OFFER_ACTIONABLE_STATES.includes(extensionOffer.state)
          ) {
            return (
              <ExtensionOfferDrawerTrigger
                key={`ExtensionOfferDrawerTrigger-${extensionOffer.id}`}
                extensionOfferId={extensionOffer.id}
              >
                <Gutter direction="bottom" size="md">
                  <OfferPreviewCard
                    key={extensionOffer.id}
                    action="sent"
                    actionedAt={
                      <TimeWithTimeZone
                        date={extensionOffer.stateChangedAt}
                        variant="long_format"
                      />
                    }
                    candidateName={assignment.worksProfile.fullName}
                    actionerName={assignment.organization.name}
                    isExtension={true}
                  >
                    <ButtonGroup>
                      {extensionOffer.canRetract.value && (
                        <Button size="md" danger>
                          Retract
                        </Button>
                      )}
                      <Button primary size="md">
                        View Extension Offer
                      </Button>
                    </ButtonGroup>
                  </OfferPreviewCard>
                </Gutter>
              </ExtensionOfferDrawerTrigger>
            );
          }

          if (extensionOffer.state === 'pending') {
            return (
              <OrganizationOnly key={`OrgOnly-${extensionOffer.id}`}>
                <Gutter direction="bottom" size="md">
                  <OfferPreviewCard
                    key={extensionOffer.id}
                    action="sent"
                    actionedAt={
                      <TimeWithTimeZone
                        date={extensionOffer.stateChangedAt}
                        variant="long_format"
                      />
                    }
                    candidateName={assignment.worksProfile.fullName}
                    actionerName={assignment.organization.name}
                    isExtension={true}
                  >
                    <ButtonGroup>
                      <OrganizationOnly>
                        <ExtensionOfferActionRetract
                          disabled={!extensionOffer.canRetract.value}
                          extensionOfferId={extensionOffer.id}
                          buttonText="Retract"
                        />
                      </OrganizationOnly>
                      <ExtensionOfferDrawerTrigger
                        key={`ExtensionOfferDrawerTrigger-${extensionOffer.id}`}
                        extensionOfferId={extensionOffer.id}
                      >
                        <Button primary size="md">
                          View Extension Offer
                        </Button>
                      </ExtensionOfferDrawerTrigger>
                    </ButtonGroup>
                  </OfferPreviewCard>
                </Gutter>
              </OrganizationOnly>
            );
          }

          if (extensionOffer.state === 'rejected_by_approver') {
            return (
              <ExtensionOfferDrawerTrigger
                key={`ExtensionOfferDrawerTrigger-${extensionOffer.id}`}
                extensionOfferId={extensionOffer.id}
              >
                <Gutter direction="bottom" size="md">
                  <OfferPreviewCard
                    action={extensionOffer.state}
                    actionedAt={
                      <TimeWithTimeZone
                        date={assignment.offer.stateChangedAt}
                        variant="long_format"
                      />
                    }
                    candidateName={assignment.worksProfile.fullName}
                    actionerName={assignment.supplier.name}
                    isExtension={true}
                  >
                    <Gutter direction="right" size="md">
                      <Icon
                        icon="angleRight"
                        kind="light"
                        size="xl"
                        color="elm"
                      />
                    </Gutter>
                  </OfferPreviewCard>
                </Gutter>
              </ExtensionOfferDrawerTrigger>
            );
          }

          if (
            Object.values(EXTENSION_OFFER_END_STATES).includes(
              extensionOffer.state
            )
          ) {
            return (
              <ExtensionOfferDrawerTrigger
                key={`ExtensionOfferDrawerTrigger-${extensionOffer.id}`}
                extensionOfferId={extensionOffer.id}
              >
                <Gutter direction="bottom" size="md">
                  <OfferPreviewCard
                    key={`OfferPreviewCard-${extensionOffer.id}-rejected-retracted`}
                    action={extensionOffer.state}
                    actionedAt={
                      <TimeWithTimeZone
                        date={extensionOffer.stateChangedAt}
                        variant="long_format"
                      />
                    }
                    candidateName={assignment.worksProfile.fullName}
                    actionerName={assignment.organization.name}
                    isExtension={true}
                  >
                    <Gutter direction="right" size="md">
                      <Icon
                        icon="angleRight"
                        kind="light"
                        size="xl"
                        color="elm"
                      />
                    </Gutter>
                  </OfferPreviewCard>
                </Gutter>
              </ExtensionOfferDrawerTrigger>
            );
          }
        })}

      <AssignmentExtensionSummary
        key={`AssignmentExtensionSummary-${assignment.id}`}
        staffingRequestAssignmentId={assignment.id}
        sourceHistory={assignment.sourceHistory}
        assignmentState={assignment.state}
        estimatedBudget={assignment.sourceEstimatedBudget}
        terms={assignment.notVoidedSourceAssignmentTerms}
        percentageComplete={assignment.sourcePercentageComplete}
        startDate={assignment.sourceStartDate}
        endDate={assignment.sourceEndDate}
        lengthInWeeks={assignment.lengthInWeeks}
        isExtension={false}
      >
        <Gutter direction="bottom" size="md">
          <OfferPreviewCard
            key={`OfferPreviewCard-${assignment.id}`}
            action="accepted"
            actionedAt={
              <TimeWithTimeZone
                date={assignment.offer.stateChangedAt}
                variant="long_format"
              />
            }
            candidateName={assignment.worksProfile.fullName}
            actionerName={assignment.supplier.name}
          >
            <Link to={offerPreviewDrawerUrl}>
              <Button size="md">View Offer</Button>
            </Link>
          </OfferPreviewCard>
        </Gutter>
      </AssignmentExtensionSummary>
    </div>
  );
}

function ExtensionOfferDrawerTrigger({ extensionOfferId, children }) {
  const extensionOfferDrawerUrl = buildDialogUrl('extension-offer-drawer', {
    props: { extensionOfferId },
  });

  return <Link to={extensionOfferDrawerUrl}>{children}</Link>;
}

ExtensionOfferDrawerTrigger.propTypes = {
  extensionOfferId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const AssignmentStateReasonFragment = gql`
  fragment AssignmentStateReasonFragment on StaffingRequestAssignment {
    id
    state
    cancelledReason
    cancelledDetail
    stateChangedAt
  }
`;

AssignmentPane.fragments = {
  staffingRequestAssignment: gql`
    fragment AssignmentPaneStaffingRequestAssignmentFragment on StaffingRequestAssignment {
      id
      neverStarted
      estimatedBudget
      sourceEstimatedBudget
      sourcePercentageComplete
      sourceEffectiveAt
      sourceStartDate
      sourceEndDate
      stateChangedAt
      finalEndDate
      cancelledByUserName
      sourceHistory {
        ...HistoryInfoFragment
      }
      canExtend {
        value
      }
      worksProfile {
        id
        fullName
      }
      staffingRequestApplication {
        id
        ...StaffingRequestApplicationInfo
      }
      supplier {
        id
        name
      }
      organization {
        id
        name
      }
      offer {
        id
        stateChangedAt
      }
      activeExtensionOffer {
        id
      }
      notVoidedSourceAssignmentTerms {
        id
        employmentType
        staffingRequestAssignment {
          id
          organization {
            id
            billingEnabled
          }
          worksBillingConfig {
            id
          }
        }
      }
      extensionOffers {
        id
        sourceHistory {
          ...HistoryInfoFragment
        }
        ...ExtensionOfferFragment
      }
      ...AssignmentStateReasonFragment
    }
    ${AssignmentStateReasonFragment}
    ${HistoryInfoFragment}
    ${STAFFING_REQUEST_APPLICATION_INFO_FRAGMENT}
    ${EXTENSION_OFFER_FRAGMENT}
  `,
};

AssignmentPane.propTypes = {
  assignment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    estimatedBudget: PropTypes.number.isRequired,
    finalEndDate: PropTypes.string.isRequired,
    neverStarted: PropTypes.bool.isRequired,
    sourceEstimatedBudget: PropTypes.number.isRequired,
    sourcePercentageComplete: PropTypes.number.isRequired,
    sourceStartDate: PropTypes.string.isRequired,
    sourceEffectiveAt: PropTypes.string.isRequired,
    sourceEndDate: PropTypes.string.isRequired,
    sourceHistory: PropTypes.array,
    lengthInWeeks: PropTypes.number.isRequired,
    stateChangedAt: PropTypes.string.isRequired,
    cancelledReason: PropTypes.string,
    cancelledDetail: PropTypes.string,
    cancelledByUserName: PropTypes.string,
    notVoidedSourceAssignmentTerms: PropTypes.array,
    activeExtensionOffer: PropTypes.object,
    staffingRequestApplication: PropTypes.object,
    canExtend: PropTypes.shape({
      value: PropTypes.bool,
    }),
    worksProfile: PropTypes.shape({
      id: PropTypes.string.isRequired,
      fullName: PropTypes.string.isRequired,
    }),
    supplier: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    organization: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    offer: PropTypes.shape({
      id: PropTypes.string.isRequired,
      stateChangedAt: PropTypes.string.isRequired,
    }),
    extensionOffers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        sourceEffectiveAt: PropTypes.string,
        sourceStartDate: PropTypes.string,
      })
    ),
  }),
};
